import Input from 'ui/mobile-ds/Input';
import { Search, Location, LocationAvailable } from 'ui/mobile-ds/icons';
import { Tabs, TabsContent } from 'ui/mobile-ds/Tabs';
import HospitalListCard from '@/components/network-hospital/HospitalListCard';
import { Button } from 'ui/mobile-ds';
import {
  Fragment,
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useLocation from '../../../hooks/useLocation';
import { useAppContext } from '../../../context/AppContext';
import LocationAccessPrompt from '../../../components/network-hospital/LocationAccessPrompt';
import { Layout } from '@/components/Common/Layout';
import {
  MutationState,
  useInfiniteQuery,
  useMutation,
  useMutationState,
  useQuery,
} from '@tanstack/react-query';
import {
  EHospitalStatus,
  EMessageType,
  INetworkHospitalConfig,
  INetworkHospitalResponse,
} from '@/types';
import { useInView } from 'react-intersection-observer';
import get from 'lodash.get';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import clsx from 'clsx';
import { IClaimCreateStepProps } from '../../create';
import { Cross } from 'ui/retail-ds/icons';
import useClaimCreateStore, {
  ClaimCreateFormSchema,
  EClaimCreateFlowSteps,
} from '@/store/claimCreateStore';
import {
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
  UseFormSetValue,
} from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Stepper from '@/components/Common/Stepper/Stepper';
import {
  getNetworkHospitalConfig,
  networkHospitalQuery,
} from '@/queries/networkHospital.queries';
import { getAllBenefitsForMember } from '@/queries/memberDetails.queries';
import { ExclamationTriangle } from 'ui/icons';
import { APP_DEEPLINK_OUTSIDE_BASE_URL, TRACKER_EVENTS } from '@/constants';
import { PageHeader } from '@/components/Common/Layout/Header/PageHeader';
import useBreakpoints from '@/hooks/useBreakpoints';
import Cookies from 'js-cookie';
import { Loader } from '@/components/Common/Layout/Loader';
import { INHTrackingHospitalStats } from '@/utils/tracking/networkHospital.tracking';
import getNHTrackingStats from '@/utils/tracking/networkHospital.tracking';
import { getDimensions, postMessageToParent, track } from '@/utils';
import AppConfig from '@/AppConfig';
import toast from 'react-hot-toast';
import CantFindHospitalWidget from '@/components/network-hospital/CantFindHospitalWidget';
import CantFindHospitalModal from '@/components/network-hospital/CantFindHospitalModal';
import useModalRoute from '@/hooks/useModalRoute';
import useMobileBackPress from '@/hooks/useMobileBackPress';

const PAGE_SIZE = 10;

const isMutationDataValid = (
  latestMutation: MutationState<
    INetworkHospitalConfig,
    Error,
    unknown,
    unknown
  >,
  insurerName: string,
  tpaName: string,
) => {
  return (
    latestMutation?.data != null &&
    // @ts-ignore
    latestMutation?.variables?.insurer === insurerName &&
    // @ts-ignore
    latestMutation?.variables?.tpa === tpaName
  );
};

const Schema = ClaimCreateFormSchema.pick({
  hospitalName: true,
  hospitalId: true,
  manualHospitalName: true,
});
type TSchema = z.infer<typeof Schema>;

const ErrorState = ({ onClick }: { onClick?: () => void }) => {
  return (
    <div className="flex flex-col gap-2 items-center max-w-[280px] mx-auto my-auto">
      <ExclamationTriangle />
      <p className="text-gray-700 font-chromatica text-xl font-bold leading-[120%] text-center">
        We couldn’t load the hospital data
      </p>
      <p className="text-mobile-text-hierarchy-1 text-sm leading-[160%] text-center">
        Something went wrong on our end
      </p>
      {onClick ? (
        <Button className="w-full max-w-[256px] mt-6" onClick={onClick}>
          Refresh
        </Button>
      ) : null}
    </div>
  );
};

const EmptyState = ({
  isInCreateFlow,
  onOpenChange,
  setValue,
  onSubmit,
}: {
  isInCreateFlow?: boolean;
  onOpenChange: (open?: boolean) => void;
  setValue: UseFormSetValue<{
    hospitalName?: string | undefined;
    hospitalId?: string | null | undefined;
    manualHospitalName?: string | null | undefined;
  }>;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
}) => {
  return (
    <div className="flex flex-col gap-2 items-center max-w-[330px] mx-auto my-auto pb-20">
      <ExclamationTriangle />
      <p className="text-gray-700 font-chromatica text-base md:text-xl font-bold leading-[120%] text-center">
        No matches found in your insurer's cashless hospital network
      </p>
      {isInCreateFlow && (
        <>
          <Button
            onClick={() => {
              onOpenChange(true);
            }}
            intent={'secondary'}
            className="mt-8 text-sm"
          >
            Enter Hospital Manually
          </Button>
          <Button
            type="submit"
            intent={'tertiary'}
            onClick={() => {
              // set both manual and hospitalName to a 'DUMMY' string
              // and also set hospital id to null
              // so that no hospital is selected
              // we'll check for the DUMMY string and remove it while API call
              setValue('manualHospitalName', '');
              setValue('hospitalName', '');
              setValue('hospitalId', null);
              onSubmit();
            }}
          >
            I haven't finalised the hospital yet
          </Button>
        </>
      )}
    </div>
  );
};

const NetworkHospitalsV2 = ({
  isInCreateFlow,
  submitFlow,
}: IClaimCreateStepProps) => {
  const {
    location,
    toggleLocation,
    isFetchingLocation,
    isLocationRejected,
    getLocation,
    initLocation,
  } = useLocation();

  const { register, handleSubmit, watch, setValue } = useForm<TSchema>({
    resolver: zodResolver(Schema),
  });

  const watchHospitalId = watch('hospitalId');
  const isLocationAvailable = useMemo(
    () => location !== null && isFetchingLocation === false,
    [location, isFetchingLocation],
  );
  const { isWebView } = useAppContext();

  const [selectedTab, setSelectedTab] = useState<EHospitalStatus>(
    EHospitalStatus.NETWORK,
  );
  const setCurrentStep = useClaimCreateStore((state) => state.setCurrentStep);
  const formState = useClaimCreateStore((state) => state.formState);
  const setFormState = useClaimCreateStore((state) => state.setFormState);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, 1000);
  const { ref: paginationRef, inView: isNextPageLoaderVisible } = useInView();

  useMobileBackPress({
    onBackPress: () => {
      setCurrentStep(EClaimCreateFlowSteps.COLLECT_MORE_INFO);
    },
    isWebView,
  });

  const inputRef = useRef<HTMLInputElement | null>(
    null,
  ) as MutableRefObject<HTMLInputElement | null>;
  const [isInputFocused, setInputFocus] = useState(false);

  const { isOpen, onOpenChange } = useModalRoute({
    additions: {
      scfhm: 'true',
    },
  });

  const inputRefCallback = (node: HTMLInputElement) => {
    inputRef.current = node;
    const handleFocus = () => {
      const backBarHeight = getDimensions('claims-back-bar')?.height ?? 0;
      if (window.scrollY < 200) {
        window?.scrollTo({
          top: backBarHeight / 2,
          behavior: 'smooth',
        });
      }
      setInputFocus(true);
    };
    const handleBlur = () => {
      setInputFocus(false);
    };
    if (node) {
      node.addEventListener('focus', handleFocus);
      node.addEventListener('blur', handleBlur);
    } else if (inputRef.current) {
      inputRef.current.removeEventListener('focus', handleFocus);
      inputRef.current.removeEventListener('blur', handleBlur);
    }
  };

  useEffect(() => {
    const handleBlur = (event: TouchEvent) => {
      const targetTouches = event?.targetTouches;
      for (let i = 0; i < targetTouches?.length; i++) {
        if (targetTouches?.[i]?.target !== inputRef?.current) {
          inputRef?.current?.blur?.();
        }
      }
    };

    window.addEventListener('touchstart', handleBlur);

    return () => {
      window.removeEventListener('touchstart', handleBlur);
    };
  }, []);

  const isEmbedded = window.parent !== window;

  const isBenefitDetailsQueryEnabled =
    !isWebView && !isInCreateFlow && !isEmbedded;

  const {
    data: gmcBenefit,
    isLoading: isBenefitDetailsLoading,
    isSuccess: isBenefitDetailsSuccessful,
    isError: isBenefitDetailsError,
    error: benefitDetailsError,
    refetch: refetchBenefitDetails,
  } = useQuery({
    queryKey: getAllBenefitsForMember(['insurance']).queryKey,
    queryFn: async (...args) => {
      const allBenefits = await getAllBenefitsForMember(['insurance']).queryFn(
        ...args,
      );

      const gmcBenefit = allBenefits?.find((benefit) => {
        return (
          benefit.benefitType === 'gmc' ||
          benefit.benefitType === 'gmc-parent-only'
        );
      });

      return gmcBenefit;
    },
    enabled: isBenefitDetailsQueryEnabled,
  });

  useEffect(() => {
    if (isBenefitDetailsSuccessful) {
      Cookies.set('nh-insurer-handle', gmcBenefit?.benefitProviderHandle ?? '');
      Cookies.set('nh-tpa', gmcBenefit?.tpaName ?? '');
    }
  }, [gmcBenefit, isBenefitDetailsSuccessful]);

  const insurerName = Cookies.get('nh-insurer-handle') ?? '';
  const tpaName = Cookies.get('nh-tpa') ?? '';

  const {
    data: newNHData,
    status: nhConfigStatus,
    mutate: fetchNHConfig,
  } = useMutation({
    ...getNetworkHospitalConfig(insurerName, tpaName),
  });

  const mutationState = useMutationState<
    MutationState<INetworkHospitalConfig, Error, unknown, unknown>
  >({
    filters: {
      mutationKey: getNetworkHospitalConfig(insurerName, tpaName).mutationKey,
      status: 'success',
    },
  });

  const latestMutation = mutationState?.[mutationState?.length - 1];

  const [insurerConfigData, setInsurerConfigData] = useState(
    isMutationDataValid(latestMutation, insurerName, tpaName)
      ? latestMutation?.data
      : newNHData,
  );

  const isFetchingHospitalConfig = nhConfigStatus === 'pending';
  const isHospitalConfigFetched =
    nhConfigStatus !== 'pending' && insurerConfigData?.enabled != null;

  useEffect(() => {
    const isConfigQueryEnabled =
      (!isInCreateFlow ||
        !isMutationDataValid(latestMutation, insurerName, tpaName)) &&
      insurerConfigData?.enabled == null;
    if (isConfigQueryEnabled) {
      fetchNHConfig(
        { insurer: insurerName, tpa: tpaName },
        {
          onSuccess: (data) => {
            setInsurerConfigData(data);
          },
        },
      );
    }
  }, [
    isInCreateFlow,
    fetchNHConfig,
    insurerName,
    tpaName,
    latestMutation,
    insurerConfigData?.enabled,
  ]);

  const trackBlackListedCTAClick = () => {
    track({
      event: TRACKER_EVENTS.NH_BLACKLISTED_CTA_CLICKED,
      data: getTrackingStats({}) ?? {},
    });
  };

  const handleBlackListedCTAClick = () => {
    trackBlackListedCTAClick();
    if (isWebView && insurerConfigData?.blackListedHospitals) {
      window.NativeBridge.callDeeplink(
        `${APP_DEEPLINK_OUTSIDE_BASE_URL}${insurerConfigData?.blackListedHospitals}`,
      );
    }
  };

  // Determines if the search query should be enabled based on the following conditions:
  // - If the debounced query has a non-empty value and the location is rejected, OR
  // - If the location is not rejected and location fetching is not in progress, AND one of the following is true:
  //    - [Standalone] Benefit details have successfully loaded and are not currently loading, and there is a valid gmcBenefit, OR
  //    - [Mobile WebView] The search is being performed in a WebView context, OR
  //    - [iframe in plum-app] The search is embedded in another context, OR
  //    - [Create Flow] Benefit details query is disabled but both insurerName and tpaName are provided
  const isSearchQueryEnabled = Boolean(
    (debouncedQuery.length > 0 && isLocationRejected) ||
      (!isLocationRejected &&
        !isFetchingLocation &&
        ((isBenefitDetailsSuccessful &&
          !isBenefitDetailsLoading &&
          Boolean(gmcBenefit)) ||
          isWebView ||
          isEmbedded ||
          (!isBenefitDetailsQueryEnabled && insurerName && tpaName))),
  );

  const {
    data,
    fetchNextPage,
    isFetchingNextPage,
    status,
    isLoading,
    isFetched,
    isRefetching,
    isError: isNetworkHospitalError,
    error: networkHospitalError,
    refetch: refetchNetworkHospitals,
  } = useInfiniteQuery<INetworkHospitalResponse>({
    enabled: isSearchQueryEnabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: Infinity,
    queryKey: networkHospitalQuery({
      insurerName,
      type: selectedTab,
      query: debouncedQuery,
      latitude: location?.latitude,
      longitude: location?.longitude,
      from: 0,
      size: PAGE_SIZE,
      tpaName,
    }).queryKey,
    queryFn: (context) => {
      return networkHospitalQuery({
        insurerName,
        type: selectedTab,
        query: debouncedQuery,
        latitude: location?.latitude,
        longitude: location?.longitude,
        from: (context.pageParam as number) ?? 0,
        size: PAGE_SIZE,
        tpaName,
      }).queryFn(context) as INetworkHospitalResponse;
    },
    getNextPageParam: (current) => {
      return current.next;
    },
    initialPageParam: 0,
  });

  useEffect(() => {
    if (isNextPageLoaderVisible) {
      fetchNextPage();
    }
  }, [fetchNextPage, isNextPageLoaderVisible]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Add your logic here, e.g., close a modal or perform any other action
        window.parent.postMessage(
          JSON.stringify({
            type: EMessageType.CLOSE_FPM_MODAL,
            data: true,
          }),
          AppConfig.PLUM_APP_HOST_URL,
        );
      }
    };

    // Add the event listener
    window.addEventListener('keydown', handleEsc);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, []);

  const hasAnyResult =
    isFetched &&
    !isLoading &&
    data?.pages?.some((page) => page.list.length > 0);

  const showLoader =
    isLoading ||
    isRefetching ||
    isFetchingLocation ||
    isFetchingHospitalConfig ||
    isBenefitDetailsLoading;

  const showError =
    isNetworkHospitalError ||
    networkHospitalError != null ||
    isBenefitDetailsError ||
    benefitDetailsError != null;

  useEffect(() => {
    if (isHospitalConfigFetched) {
      window?.NativeBridge?.onLoadComplete?.();
      initLocation();
    }
  }, [isHospitalConfigFetched]);

  const totalResults = data?.pages?.[0]?.total ?? 0;

  const showLocationAccessPrompt =
    isLocationRejected &&
    !hasAnyResult &&
    !isLoading &&
    !isFetchingNextPage &&
    query.length === 0;

  const showNextPageSpinner = isFetchingNextPage && status === 'success';

  const onSubmit: SubmitHandler<TSchema> = (data) => {
    if (isInCreateFlow) {
      setFormState({ ...data });
      submitFlow?.({
        ...formState,
        ...data,
      });
    }
  };

  const { isMobile } = useBreakpoints();

  const numberFormatter = new Intl.NumberFormat('en-IN');

  const handleBack = () => {
    if (isInCreateFlow) {
      setCurrentStep(EClaimCreateFlowSteps.COLLECT_MORE_INFO);
    } else {
      if (isWebView) {
        window?.NativeBridge?.onNativeClose?.();
      } else {
        if (isEmbedded) {
          postMessageToParent(EMessageType.CLOSE_FPM_MODAL, true);
        } else {
          navigate(-1);
        }
      }
    }
  };

  const getTrackingStats = ({
    hospitalId,
    resultPosition: hospitalIndex,
    hospitalPin,
    hospitalCity,
  }: Partial<
    Pick<
      INHTrackingHospitalStats,
      'hospitalCity' | 'hospitalId' | 'hospitalPin' | 'resultPosition'
    >
  >) =>
    getNHTrackingStats({
      isInCreateFlow: isInCreateFlow ?? false,
      insurerName,
      tpaName,
      isWebView,
      query,
      isLocationRejected,
      location,
      selectedTab,
      totalResults,
      hospitalId,
      hospitalIndex,
      hospitalPin,
      hospitalCity,
    });

  useEffect(() => {
    if (debouncedQuery) {
      const trackingStats = getTrackingStats({});
      track({
        event: TRACKER_EVENTS.NH_SEARCH_QUERY,
        data: {
          ...trackingStats.global,
          ...trackingStats.hospital,
        },
      });
    }
  }, [debouncedQuery, selectedTab]);

  useEffect(() => {
    if (selectedTab) {
      const tabElem = document.getElementById(selectedTab);
      if (tabElem) {
        tabElem?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }, [selectedTab]);

  const trackLocationToggle = () => {
    track({
      event: TRACKER_EVENTS.NH_LOCATION_TOGGLE,
      data: getTrackingStats({}) ?? {},
    });
  };

  const handleLocationToggle = () => {
    trackLocationToggle();
    toggleLocation();
  };

  return (
    <>
      <Layout.Main
        fullWidth
        hideFooter
        rootClassName="gap-3"
        containerProps={{
          className: clsx({ 'h-full max-h-full !m-0': true }),
        }}
        className="p-0"
        headerProps={{
          hidePageHeader: true,
          sticky: true,
        }}
      >
        {({ headerDimensions, getDimensions, gap, bg }) => {
          const backBarDimensions = getDimensions('claims-back-bar');

          const onError: SubmitErrorHandler<TSchema> = () => {
            toast.error('Please select a hospital to continue.', {
              id: 'create-claim-error',
              style: {
                marginBottom: getDimensions('claims-footer').height,
              },
            });
          };

          return (
            <form
              onSubmit={handleSubmit(onSubmit, onError)}
              className={clsx({
                'flex flex-col h-full': true,
                [`${bg}`]: bg,
              })}
            >
              {!isOpen && (
                <>
                  {(isLocationAvailable || query.length > 0) && (
                    <CantFindHospitalWidget onOpenChange={onOpenChange} />
                  )}
                </>
              )}
              <CantFindHospitalModal
                open={isOpen}
                onOpenChange={onOpenChange}
                isInCreateFlow={isInCreateFlow}
                onSubmit={handleSubmit(onSubmit, onError)}
                watch={watch}
                register={register}
                blacklistHospitalUrl={
                  insurerConfigData?.blackListedHospitals || ''
                }
                setValue={setValue}
                handleBlackListedCTAClick={handleBlackListedCTAClick}
              />
              {
                <PageHeader
                  fullWidth
                  bg={bg}
                  title={`${
                    isInCreateFlow
                      ? 'Select your hospital'
                      : 'Find your network hospital'
                  }`}
                  forceRenderTitleInBackBar={Boolean(
                    isMobile && isInputFocused,
                  )}
                  renderTitleInBackBar={Boolean(isMobile)}
                  backBarClassName={clsx({
                    'sticky z-20 w-full': true,
                  })}
                  backBarStyles={{
                    top: isMobile
                      ? headerDimensions.height
                      : headerDimensions.height + gap,
                  }}
                  renderOrder={['title', 'children']}
                  className={clsx({
                    '!items-start lg:mt-8 h-full': true,
                  })}
                  titleClassName="h-full"
                  childrenContainerClassName="gap-y-0 pt-3 max-w-full w-full mx-auto h-full"
                  onBack={handleBack}
                >
                  <div
                    className={clsx({
                      'sticky z-20 pt-4 sm:pt-10 mt-[1px] flex flex-col items-stretch gap-1':
                        true,
                      [`${bg}`]: bg,
                    })}
                    style={{
                      top: isMobile
                        ? headerDimensions.height + backBarDimensions.height
                        : headerDimensions.height + gap + 8 - 40,
                    }}
                  >
                    <Input
                      ref={inputRefCallback}
                      containerClassName="!border-none"
                      className={clsx({
                        'border-gray-500 placeholder:text-mobile-text-main focus:placeholder:text-gray-200 pr-20':
                          isLocationAvailable,
                        'border rounded-xl focus:outline-none focus:border-gray-500':
                          true,
                      })}
                      placeholder={
                        isLocationAvailable && !isInputFocused
                          ? 'Current Location'
                          : 'Search hospitals, city or PIN'
                      }
                      value={query}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (inputRef.current) {
                            inputRef.current?.blur?.();
                          }
                        }
                      }}
                      onChange={(e) => {
                        setQuery(e.target.value);
                      }}
                      prefixIcon={<Search />}
                      trailingIcon={
                        <div className="flex items-center gap-2">
                          {query?.length > 0 ? (
                            <button
                              onClick={() => {
                                setQuery('');
                              }}
                            >
                              <Cross className="w-6 h-6" />
                            </button>
                          ) : null}
                          <span className="w-[1px] h-[18px] bg-mobile-gray-200" />
                          <button
                            type="button"
                            disabled={isFetchingLocation}
                            onClick={handleLocationToggle}
                          >
                            {isLocationAvailable ? (
                              <LocationAvailable />
                            ) : (
                              <Location />
                            )}
                          </button>
                        </div>
                      }
                    />
                    {isLocationRejected ? (
                      <div className="flex items-center justify-between py-2 px-1">
                        <p
                          className={clsx({
                            'text-gray-700 text-sm font-medium leading-[160%]':
                              true,
                            'w-[60%]': isMobile,
                          })}
                        >
                          Use your location for more relevant results
                        </p>
                        <Button
                          type="button"
                          onClick={() => getLocation()}
                          className="p-[10px]! h-auto w-auto min-w-fit"
                        >
                          Give access
                        </Button>
                      </div>
                    ) : null}
                  </div>
                  <Tabs
                    onValueChange={(value) => {
                      setSelectedTab(value as EHospitalStatus);
                      window?.scrollTo?.(0, 0);
                    }}
                    className={clsx({
                      'md:pr-4 h-full flex flex-col font-body': true,
                      'select-none': isWebView,
                    })}
                    defaultValue={EHospitalStatus.NETWORK}
                  >
                    <TabsContent
                      id={EHospitalStatus.NETWORK}
                      value={EHospitalStatus.NETWORK}
                      className={clsx({
                        'flex flex-col': true,
                        'h-full': selectedTab === EHospitalStatus.NETWORK,
                        'pb-32':
                          selectedTab === EHospitalStatus.NETWORK && isMobile,
                      })}
                    >
                      {showLocationAccessPrompt ||
                      (query.length === 0 &&
                        !isLocationAvailable &&
                        !isFetchingLocation) ? (
                        <LocationAccessPrompt
                          blacklistHospitalUrl={
                            insurerConfigData?.blackListedHospitals || ''
                          }
                          handleBlackListedCTAClick={handleBlackListedCTAClick}
                        />
                      ) : showLoader ? (
                        <Loader
                          title={
                            isLocationAvailable && !isLocationRejected
                              ? 'Fetching nearby hospitals'
                              : ''
                          }
                        />
                      ) : showError ? (
                        <ErrorState
                          onClick={
                            isBenefitDetailsError
                              ? refetchBenefitDetails
                              : refetchNetworkHospitals
                          }
                        />
                      ) : hasAnyResult ? (
                        <>
                          <div className="mt-5" />
                          {query.length > 0 && (
                            <p className="font-chromatica text-xl font-bold text-mobile-text-main">
                              Search Results
                            </p>
                          )}
                          <p className="mb-6 text-sm text-mobile-text-hierarchy-1 leading-[160%]">
                            {numberFormatter.format(totalResults)}{' '}
                            {(totalResults ?? 0) === 1 ? 'match' : 'matches'}
                          </p>
                          {data?.pages.map((hospistalList, pageIndex) => {
                            return hospistalList.list.map(
                              (hospital, listIndex) => {
                                const hospitalIndex =
                                  pageIndex * PAGE_SIZE + listIndex;
                                const hospitalPin = hospital.pincode;
                                const hospitalCity = hospital.city;
                                const hospitalId = hospital.id;

                                return (
                                  <Fragment key={hospital.id}>
                                    <HospitalListCard
                                      query={query}
                                      getTrackingStats={() => {
                                        const trackingStats = getTrackingStats({
                                          hospitalId,
                                          resultPosition: hospitalIndex,
                                          hospitalPin,
                                          hospitalCity,
                                        });
                                        return {
                                          ...trackingStats.global,
                                          ...trackingStats.hospital,
                                        };
                                      }}
                                      hospital={{
                                        id: hospital.id,
                                        name: hospital.name,
                                        address: `${hospital.address} ${hospital.city} ${hospital.state} ${hospital.pincode}`,
                                        distance: hospital.distance
                                          ? hospital?.distance < 1000
                                            ? `${Math.round(
                                                hospital?.distance,
                                              )}m`
                                            : `${Math.round(
                                                hospital?.distance / 1000,
                                              )}km`
                                          : undefined,
                                        phone: hospital.phone,
                                        website: hospital.website,
                                        mapsUrl: get(
                                          hospital,
                                          'googleMaps.url',
                                          '',
                                        ),
                                      }}
                                      mode={isInCreateFlow ? 'select' : 'view'}
                                      selected={watchHospitalId === hospital.id}
                                      onSelect={(hospital) => {
                                        setValue(
                                          'hospitalName',
                                          hospital.name ?? '',
                                        );
                                      }}
                                      {...register('hospitalId')}
                                      value={hospital.id}
                                    />
                                    <div className="h-[1px] bg-mobile-gray-100 my-5" />
                                  </Fragment>
                                );
                              },
                            );
                          })}
                        </>
                      ) : (
                        <EmptyState
                          isInCreateFlow={isInCreateFlow}
                          onOpenChange={onOpenChange}
                          setValue={setValue}
                          onSubmit={handleSubmit(onSubmit, onError)}
                        />
                      )}

                      {hasAnyResult && status === 'success' ? (
                        <div ref={paginationRef} className="w-full pt-8" />
                      ) : null}
                      {isLocationAvailable && showNextPageSpinner ? (
                        <Loader />
                      ) : null}
                    </TabsContent>
                  </Tabs>
                </PageHeader>
              }
              <Layout.Footer
                isSticky
                className={clsx({
                  hidden: !(
                    (selectedTab === EHospitalStatus.NETWORK ||
                      selectedTab === EHospitalStatus.NON_NETWORK) &&
                    isInCreateFlow
                  ),
                  'z-20': true,
                })}
                containerClassName="flex justify-between"
              >
                <Stepper
                  className="hidden md:flex"
                  activeStep={4}
                  activeSubStep={0}
                  totalSubSteps={0}
                  totalSteps={4}
                  title="Select your hospital"
                  subTitle="Step 4/4"
                />
                <Button
                  type="submit"
                  className="ml-auto md:max-w-[260px] w-full text-base"
                  onClick={(event) => {
                    if (watchHospitalId == null) {
                      event.preventDefault();
                      toast('Please select a hospital to continue');
                    }
                  }}
                >
                  Continue
                </Button>
              </Layout.Footer>
            </form>
          );
        }}
      </Layout.Main>
    </>
  );
};

export default NetworkHospitalsV2;
